import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { useHandler } from "@redotech/react-util/hook";
import type { MessageChannelV3 } from "@redotech/redo-flows-common/rules-v3/types";
import InboxIcon from "@redotech/redo-icons/inbox-01.svg";
import {
  useUpdateAssignableChannelsMutation,
  useWorkScheduleQuery,
} from "@redotech/redo-merchant-app-common/setting/use-work-schedule-query";
import { toast } from "@redotech/redo-web/alert";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";
import { channelLabels } from "./channel-labels";
import { useChannelConnectivity } from "./use-channel-connectivity";

/** Omit `targetMongoUserId` to edit your own channels; a manager may pass another agent's. */
export const ConfigureChannelsModal = memo(function ConfigureChannelsModal({
  onClose,
  targetMongoUserId,
  title,
}: {
  onClose: () => void;
  targetMongoUserId?: MongoUserId;
  title?: string;
}) {
  const scheduleQuery = useWorkScheduleQuery({ targetMongoUserId });
  const updateChannels = useUpdateAssignableChannelsMutation(targetMongoUserId);
  const { connectedChannels, pending } = useChannelConnectivity();

  const [selected, setSelected] = useState<readonly MessageChannelV3[] | null>(
    null,
  );

  const saved = scheduleQuery.data?.schedule.assignableChannels;

  useEffect(() => {
    if (saved === undefined) {
      return;
    }
    // Null means "every channel", which presents as everything connected ticked.
    setSelected(
      saved === null ? connectedChannels : (saved as MessageChannelV3[]),
    );
  }, [saved, connectedChannels]);

  const allChecked = useMemo(
    () =>
      connectedChannels.length > 0 &&
      connectedChannels.every((channel) => selected?.includes(channel)),
    [connectedChannels, selected],
  );

  const toggleChannel = useHandler(
    (channel: MessageChannelV3, checked: boolean) => {
      setSelected((current) => {
        const base = current ?? [];
        return checked
          ? [...base, channel]
          : base.filter((value) => value !== channel);
      });
    },
  );

  const toggleAll = useHandler((checked: boolean) => {
    setSelected(checked ? connectedChannels : []);
  });

  const handleSave = useHandler(async () => {
    if (selected === null) {
      return;
    }
    // Everything ticked is stored as null so newly connected channels are opted
    // into by default rather than silently excluded.
    const channels = allChecked ? null : selected;
    try {
      await updateChannels.mutateAsync(channels);
      toast("Channels updated", { variant: "success" });
      onClose();
    } catch {
      toast("Could not update channels", { variant: "error" });
    }
  });

  const handleReset = useHandler(() => {
    setSelected(connectedChannels);
  });

  const loading = scheduleQuery.isPending || pending || selected === null;

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Save",
        onClick: handleSave,
        disabled: loading,
        pending: updateChannels.isPending,
      }}
      secondaryButton={{ text: "Reset", onClick: handleReset }}
      subtitle={
        targetMongoUserId === undefined
          ? "Choose which support tickets you can receive when you're available."
          : "Choose which support tickets this teammate can receive when they're available."
      }
      title={title ?? "Configure channels"}
      TitleIcon={InboxIcon}
    >
      {loading ? (
        <LoadingRedoAnimation />
      ) : (
        <Flex dir="column" gap="md">
          <RedoCheckbox
            label="All channels"
            setValue={(value) => toggleAll(value === true)}
            value={allChecked}
          />
          <Divider />
          {connectedChannels.length === 0 && (
            <Text fontSize="sm" textColor="secondary">
              No channels are connected yet.
            </Text>
          )}
          {connectedChannels.map((channel) => (
            <RedoCheckbox
              key={channel}
              label={channelLabels[channel]}
              setValue={(value) => toggleChannel(channel, value === true)}
              value={selected.includes(channel)}
            />
          ))}
        </Flex>
      )}
    </RedoModal>
  );
});
